import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Data } from '@angular/router';
import { PlpService } from '@services/plp.service';
import { BehaviorSubject, Observable, filter, of, switchMap } from 'rxjs';
import { ScreenDetectorBootstrapService } from '@services/app/screen-detector-bootstrap.service';
import { FiltersSectionComponent } from '../../../components/search/filters-section/filters-section.component';
import { MetaService } from '@services/app/meta.service';
import { Filters } from '@schemas/search/search-request';
import { SearchOperatorsEnum } from '@shared/enums/search-api.enum';
import { PlpCurrencyData, PlpRouteParams } from '../../listing-view/plp-definitions';
import { AppService } from '@services/app/app.service';
import { SeoSocialShareData, SeoSocialShareService } from 'ngx-seo';
import { UniversalService } from '@services/universal.service';
import { Plp } from '@schemas/plp.interface';
import { AppConfiguration } from '@schemas/app.interface';
import { environment } from '@environments/environment';

@Component({
  selector: 'tvb-mother-page',
  templateUrl: './mother-page.component.html',
  styleUrls: ['./mother-page.component.scss'],
})
export class MotherPageComponent implements OnInit {
  @ViewChild(FiltersSectionComponent) filtersSectionComponent: FiltersSectionComponent;
  public isMobile$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public title: string;
  public description: string;
  public subTitle: string;
  public subDescription: string;
  public seo_url: string;
  public plp_for: string;
  public param: string;
  public type: string = null;
  public discover?: string = null;
  public products_ids?: string = null;
  public filter?: Filters = null;
  public descriptionLoaded: boolean = false;
  public routeParamMap$: Observable<PlpRouteParams>;
  public currencyConfiguration$: Observable<PlpCurrencyData>;
  public productId: string;
  public position = 0;
  public isPDP: boolean = null;
  public appConfig: AppConfiguration;
  public is404: boolean = false;
  public isProductNotFound: boolean = false;
  public environment = environment;

  constructor(
    private route: ActivatedRoute,
    private plpService: PlpService,
    private metaService: MetaService,
    private screenSizeDetector: ScreenDetectorBootstrapService,
    private activatedRoute: ActivatedRoute,
    public appService: AppService,
    private readonly seoSocialShareService: SeoSocialShareService,
    private universalService: UniversalService
  ) {
    if (this.plpService.pageInfo.value) {
      this.title = this.plpService.pageInfo.value.title;
      this.description = this.plpService.pageInfo.value.description;
    }
  }

  ngOnInit(): void {
    this.appConfig = this.appService.getAppConfigurationValue();
    this.activatedRoute.queryParams.pipe(filter(params => params.pdp)).subscribe(params => {
      this.productId = params.pdp;
      this.position = params.position;
      this.isPDP = true;
      this.setRouteParamForPdp();
      this.setCurrencyForPdp();
    });

    if (!this.isPDP) {
      this.isMobile$ = this.screenSizeDetector.isMobile;
      this.route.data.subscribe(data => {
        this.getMotherPageDescription(data);
      });
    }
  }

  showProductNotFound($event: boolean) {
    if ($event) {
      this.isProductNotFound = $event;
      this.setPageNotFoundMetaDate();
    }
  }

  showPageNotFound($event: boolean) {
    if ($event) {
      this.is404 = $event;
      this.setPageNotFoundMetaDate();
    }
  }

  removeTag($event) {
    this.filtersSectionComponent.removeTag($event);
  }

  private getMotherPageDescription(data: Data): void {
    this.metaService.getStaticPageMeta(data.plpFor);
    this.setMotherPageSettings(data.plpFor);
    this.getMetaData(data);
  }

  private applyFilters() {
    if (this.plp_for === 'discover') {
      this.discover = this.seo_url;
      this.filter = {
        field: 'discover',
        operator: SearchOperatorsEnum.EQUALS,
        value: this.seo_url,
      };
    }
  }

  setPageNotFoundMetaDate() {
    this.title = 'Page Not Found | The Vintage Bar';
    this.description = 'Page Not Found';
    this.plpService.pageInfo.next({ title: this.title, description: this.description });
    const seoData: SeoSocialShareData = {
      title: this.title,
      description: this.description,
      url: environment.baseRemoteUrl,
    };

    this.seoSocialShareService.setData(seoData);
  }

  private getMetaData(data: Data) {
    const request = {
      language: this.getCurrentLanguage(),
      type: this.type ?? data.plpFor,
      url: this.param,
    };

    switch (data.plpFor) {
      case 'sale': {
        this.metaService.getListingPageMeta(data.plpFor, null, null);
        this.descriptionLoaded = true;
        this.title = 'sale.top.title';
        this.description = 'sale.top.description';
        this.plpService.pageInfo.next({ title: this.title, description: this.description });
        break;
      }
      case 'new-in': {
        this.metaService.getListingPageMeta(data.plpFor, null, null);
        this.descriptionLoaded = true;
        this.title = 'new_in.title';
        this.description = 'new_in.description';
        this.plpService.pageInfo.next({ title: this.title, description: this.description });
        break;
      }
      case 'discover': {
        this.metaService.getListingPageMeta(data.plpFor, this.route.snapshot.params?.discover_seo_url);
        this.setMotherPageDescription(request);
        break;
      }
      case 'designer': {
        this.metaService.getListingPageMeta(data.plpFor, this.route.snapshot.params?.designer_seo_url);
        this.setMotherPageDescription(request);
        break;
      }
      case 'style':
        this.metaService.getListingPageMeta('style-motherpage', this.route.snapshot.params?.styles_seo_url);
        this.setMotherPageDescription(request);
        break;
      case 'child-category': {
        this.metaService.getListingPageMeta(
          data.plpFor,
          this.route.snapshot.params?.parent_category_seo_url,
          this.route.snapshot.params?.child_category_seo_url
        );
        this.setMotherPageDescription(request);
        break;
      }
      case 'category': {
        this.metaService.getListingPageMeta(data.plpFor, this.route.snapshot.params?.parent_category_seo_url);
        this.setMotherPageDescription(request);
        break;
      }
      case 'searchItems': {
        this.metaService.getListingPageMeta(data.plpFor, this.route.snapshot.params?.search_query);
        this.setMotherPageDescription(request);
        break;
      }
      case 'default-allproducts': {
        this.metaService.getListingPageMeta(data.plpFor);
        this.descriptionLoaded = true;
        break;
      }
      default: {
        this.metaService.getListingPageMeta(data.plpFor).subscribe(
          data => {
            this.title = data.list.name;
            this.description = data.list.sub_title;
            this.plpService.pageInfo.next({ title: this.title, description: this.description });
            this.descriptionLoaded = true;
          },
          error => {
            this.descriptionLoaded = true;
          }
        );
      }
    }
  }

  getCurrentLanguage() {
    switch (this.appConfig?.languageShortName) {
      case 'se':
        return 'sv';
      case 'dk':
        return 'da';
      case '':
      case null:
        return 'en';
      default:
        return this.appConfig?.languageShortName;
    }
  }

  setMotherPageDescription(request: any) {
    this.plpService.getMotherPageDescription(request).subscribe(
      data => {
        if (data) {
          this.applySeoData(data);
          this.title = data.list.translatedTitle;
          this.description = data.list.translatedContent;
          this.subTitle = data.list.translatedSubTitle;
          this.subDescription = data.list.translatedSubContent;
          this.plp_for = request.type;
          this.seo_url = data.list.seo_url;
          this.applyFilters();
          this.plpService.pageInfo.next({ title: this.title, description: this.description });
        }

        this.descriptionLoaded = true;
      },
      error => {
        this.descriptionLoaded = true;
        this.title = null;
        this.description = null;
        this.plpService.pageInfo.next({ title: this.title, description: this.description });
      }
    );
  }

  applySeoData(data: any) {
    const canonicalUrl = this.universalService.getApplicationUrl(true) + this.universalService.getApplicationPathname();
    const seoData: SeoSocialShareData = {
      title: data.list.translatedMetaTitle ?? data.list.meta_title,
      description: data.list.translatedMetaDescription ?? data.list.meta_description,
      url: canonicalUrl,
    };

    this.seoSocialShareService.setData(seoData);
    this.seoSocialShareService.setCanonicalUrl(canonicalUrl);
  }

  private setMotherPageSettings(plpFor: any) {
    switch (plpFor) {
      case 'designer':
        this.param = this.route.snapshot.params.designer_seo_url;
        break;
      case 'style':
        this.param = this.route.snapshot.params.styles_seo_url;
        break;
      case 'discover':
        this.param = this.route.snapshot.params.discover_seo_url;
        break;
      case 'category':
        this.param = this.route.snapshot.params.parent_category_seo_url;
        this.type = 'categories';
        break;
      case 'sub':
        this.param = this.route.snapshot.params.child_category_seo_url;
        break;
      case 'child-category':
        this.param = this.route.snapshot.params.child_category_seo_url;
        this.type = 'categories';
        break;
      case 'searchItems':
        this.param = this.route.snapshot.params.search_query;
        this.type = 'designer';
        break;
    }
  }

  setCurrencyForPdp() {
    this.currencyConfiguration$ = this.appService.getAppConfigurationObservable().pipe(
      switchMap(appConfig => {
        if (appConfig) {
          return of({
            plpCurrency: appConfig.currencyCode,
            plpCurrencySymbol: appConfig.currencySymbol,
          });
        }
        else {
          return of({
            plpCurrency: 'USD',
            plpCurrencySymbol: '$',
          });
        }
      })
    );
  }

  setRouteParamForPdp() {
    this.routeParamMap$ = this.activatedRoute.paramMap.pipe(
      switchMap(params => {
        return of({
          parent_category_seo_url: params.get('parent_category_seo_url'),
          child_category_seo_url: params.get('child_category_seo_url'),
          designer_seo_url: params.get('designer_seo_url'),
          discover_seo_url: params.get('discover_seo_url'),
          seller_seo_url: params.get('seller_seo_url'),
          styles_seo_url: params.get('styles_seo_url'),
          search_query: params.get('search_query'),
        });
      })
    );
  }
}

export interface PageMetaData {
  message: string;
  list: List;
}

export interface List {
  meta_title: string;
  meta_description: string;
  hrefLangs: HrefLangs;
  name?: string;
  sub_title?: string;
}

export interface HrefLangs {
  en: string;
  fr: string;
  it: string;
  es: string;
  dk: string;
  se: string;
  de: string;
}
